import { HocuspocusProvider } from "@hocuspocus/provider";
import Quill, { type Op } from "quill";
import QuillCursors from "quill-cursors";
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { QuillBinding } from "y-quill";
import * as Y from "yjs";
import "quill/dist/quill.snow.css";
import "quill-cursors/css";
import { runtimeConfig } from "../config";
import { currentUser } from "../core/session";

// This is the same stack used by the dump: Quill's Delta model is stored in a
// named Y.Text and Hocuspocus transports binary Yjs updates over /ws/.
Quill.register("modules/cursors", QuillCursors as never);

const toolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  ["blockquote", "code-block", "link"],
  ["clean"],
];

export function RichTextEditor(props: {
  value: string;
  resetKey?: string | number;
  disabled?: boolean;
  placeholder?: string;
  roomName?: string;
  onChange: (value: string) => void;
  onAutoSave?: () => Promise<void>;
}) {
  let host: HTMLDivElement | undefined;
  let quill: Quill | undefined;
  let provider: HocuspocusProvider | undefined;
  let binding: QuillBinding | undefined;
  let ydoc: Y.Doc | undefined;
  let resetKey: string | number | undefined;
  let initialDelta: unknown;
  let dirtyFromUser = false;
  let suppressChange = false;
  const [dirty, setDirty] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [lastSaved, setLastSaved] = createSignal<Date | null>(null);

  const save = async () => {
    if (!dirtyFromUser || !props.onAutoSave || saving()) return;
    setSaving(true);
    try {
      await props.onAutoSave();
      dirtyFromUser = false;
      setDirty(false);
      setLastSaved(new Date());
    } finally {
      setSaving(false);
    }
  };

  const emitDelta = () => {
    if (!quill || suppressChange) return;
    props.onChange(JSON.stringify(quill.getContents()));
    dirtyFromUser = true;
    setDirty(true);
  };

  const setInitialContents = () => {
    if (!quill || initialDelta === undefined) return;
    suppressChange = true;
    quill.setContents(toDelta(initialDelta).ops as Op[], "silent");
    suppressChange = false;
  };

  onMount(() => {
    if (!host) return;
    resetKey = props.resetKey;
    initialDelta = props.value;
    quill = new Quill(host, {
      theme: "snow",
      readOnly: !!props.disabled,
      placeholder: props.placeholder ?? "Start typing here...",
      modules: {
        toolbar: props.disabled ? false : toolbar,
        cursors: {},
        history: { delay: 2_000, maxStack: 500, userOnly: true },
      },
    });
    quill.on("text-change", (_delta, _old, source) => {
      if (source === "user") emitDelta();
    });

    if (props.roomName && !props.disabled && currentUser()?.accessToken) {
      ydoc = new Y.Doc();
      provider = new HocuspocusProvider({
        url: runtimeConfig.wsOrigin,
        name: props.roomName,
        document: ydoc,
        token: currentUser()!.accessToken,
        onAuthenticationFailed: () => {
          console.error("[MDT] Could not authenticate collaborative editor");
        },
      });
      provider.setAwarenessField("user", {
        name: `${currentUser()!.firstName} ${currentUser()!.lastName}`,
        color: randomCursorColor(),
      });
      const text = ydoc.getText(props.roomName);
      binding = new QuillBinding(text, quill, provider.awareness ?? undefined);
      provider.on("synced", () => {
        // Match the dump: the initial template is inserted only into an empty
        // collaborative room, never over an existing shared document.
        if (text.length === 0) setInitialContents();
        quill?.enable(!props.disabled);
      });
      quill.disable();
    } else {
      setInitialContents();
    }

    const timer = window.setInterval(() => void save(), 30_000);
    onCleanup(() => {
      window.clearInterval(timer);
      binding?.destroy();
      provider?.destroy();
      ydoc?.destroy();
      binding = undefined;
      provider = undefined;
      ydoc = undefined;
      quill = undefined;
    });
  });

  // Record detail components may reuse the editor instance while switching
  // records. Reset only for a genuinely different room/key and never clobber
  // unsaved local edits.
  const syncValue = () => {
    if (!quill || dirtyFromUser || props.resetKey === resetKey) return;
    resetKey = props.resetKey;
    initialDelta = props.value;
    if (!provider) setInitialContents();
  };

  createEffect(() => {
    props.resetKey;
    props.value;
    syncValue();
  });

  return (
    <div class="flex min-h-0 flex-1 flex-col gap-2">
      <Show when={props.onAutoSave}>
        <div class="flex min-h-6 items-center justify-end text-xs text-white-55">
          {saving()
            ? "Saving..."
            : dirty()
              ? "Unsaved changes"
              : lastSaved()
                ? `Saved ${lastSaved()!.toLocaleTimeString()}`
                : "Autosave: 30s"}
        </div>
      </Show>
      <div ref={host} class="dump-quill min-h-[18rem] flex-1" />
      <button
        type="button"
        class="self-end bg-green-gradient px-3 py-1 text-xs disabled:opacity-40"
        disabled={!dirty() || saving() || !props.onAutoSave}
        onClick={() => void save()}
      >
        Save now
      </button>
    </div>
  );
}

function randomCursorColor(): string {
  const colors = ["#3b82f6", "#ef4444", "#f59e0b", "#22c55e"];
  return colors[Math.floor(Math.random() * colors.length)]!;
}

function toDelta(value: unknown): { ops: unknown[] } {
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed && typeof parsed === "object" && "ops" in parsed && Array.isArray((parsed as { ops?: unknown }).ops)) {
        return parsed as { ops: unknown[] };
      }
    } catch {
      // Legacy records are plain text.
    }
    return { ops: [{ insert: value.endsWith("\n") ? value : `${value}\n` }] };
  }
  if (value && typeof value === "object" && "ops" in value && Array.isArray((value as { ops?: unknown }).ops)) {
    return value as { ops: unknown[] };
  }
  return { ops: [{ insert: "\n" }] };
}
